import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { Bell, Inbox, UserCheck } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { api, ensureData } from '@/shared/api/client'
import { can, type CurrentAdmin } from '@/shared/auth/permissions'
import { Button } from '@/shared/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/popover'

/** Contagens de pendências; as chaves começam pelo recurso para que as telas, ao invalidá-lo, atualizem o sino. */
const pendingAccessRequests = {
  queryKey: ['access-requests', 'pending-count'] as const,
  queryFn: async () =>
    ensureData(await api.GET('/access-requests', { params: { query: { 'filter[status]': 'pending' } } })).meta
      .total,
  staleTime: 60_000,
}

const pendingUsers = {
  queryKey: ['users', 'pending-count'] as const,
  queryFn: async () =>
    ensureData(await api.GET('/users', { params: { query: { 'filter[status]': 'pending' } } })).meta.total,
  staleTime: 60_000,
}

interface NotificationItem {
  key: string
  icon: ReactNode
  text: string
  to: string
  search: Record<string, string>
}

/** Sino do topo: pendências que dependem de um administrador (cadastros e solicitações de acesso). */
export function NotificationsMenu({ admin }: { admin: CurrentAdmin }) {
  const { t } = useTranslation()
  const enabled = can(admin, 'users.view')

  const requests = useQuery({ ...pendingAccessRequests, enabled })
  const users = useQuery({ ...pendingUsers, enabled })

  const items: NotificationItem[] = []
  if (users.data) {
    items.push({
      key: 'users',
      icon: <UserCheck aria-hidden />,
      text: t('notificationsMenu.pendingUsers', { count: users.data }),
      to: '/users',
      search: { status: 'pending' },
    })
  }
  if (requests.data) {
    items.push({
      key: 'requests',
      icon: <Inbox aria-hidden />,
      text: t('notificationsMenu.pendingRequests', { count: requests.data }),
      to: '/access-requests',
      search: { status: 'pending' },
    })
  }

  return (
    <Popover>
      <PopoverTrigger
        render={
          <Button
            variant="outline"
            size="icon"
            className="relative text-muted-foreground"
            aria-label={
              items.length > 0
                ? t('notificationsMenu.labelWithCount', { count: items.length })
                : t('notificationsMenu.label')
            }
          />
        }
      >
        <Bell />
        {items.length > 0 ? (
          <span
            aria-hidden
            className="absolute top-2 right-2.5 size-2.5 rounded-full border-2 border-card bg-destructive"
          />
        ) : null}
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <p className="border-b px-4 py-3 text-xs font-extrabold tracking-[0.08em] text-muted-foreground uppercase">
          {t('notificationsMenu.label')}
        </p>
        {items.length === 0 ? (
          <p className="px-4 py-6 text-center text-sm text-muted-foreground">
            {t('notificationsMenu.empty')}
          </p>
        ) : (
          <ul className="p-1">
            {items.map((item) => (
              <li key={item.key}>
                <Link
                  to={item.to}
                  search={item.search}
                  className="flex items-center gap-3 rounded-md px-3 py-2.5 text-sm hover:bg-accent hover:text-accent-foreground [&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:text-warning"
                >
                  {item.icon}
                  {item.text}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </PopoverContent>
    </Popover>
  )
}
